import React from 'react'
import PropTypes from 'prop-types'

// Chakra Components
import {
    Box,
    Text,
    Flex,
    Stack,
    Image,
    Heading,
    Divider,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useProducts} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {FormattedMessage} from 'react-intl'
const GiftCertificateItemSummary = ({shipmentData, giftCertificateItems = []}) => {
    const cartStyles = useMultiStyleConfig('CartStyles')

    const {
        app: {commerceAPI}
    } = getConfig()
    const {eGiftCards} = commerceAPI.storefront || {}

    // Default config fallback
    const defaultConfigs = {
        amounts: [25, 50, 75, 100, 150, 200],
        products: {}
    }

    const parseConfigs = () => {
        if (!eGiftCards) return defaultConfigs
        try {
            const parsed = JSON.parse(eGiftCards)
            return parsed.amounts && parsed.products ? parsed : defaultConfigs
        } catch (err) {
            console.warn('Error parsing eGiftCards JSON:', err.message)
            return defaultConfigs
        }
    }

    const giftCertConfigs = parseConfigs()

    const productIds = giftCertificateItems?.map(({c_productID}) => c_productID).join(',') ?? ''

    // Fetch product data when selectedId changes
    const {data} = useProducts({
        parameters: {
            ids: productIds
        }
    })

    const products = data?.data || []

    const giftCertificates = giftCertificateItems.map((item) => {
        const product = products.find((p) => p.id === item.c_productID)

        const image = giftCertConfigs.products[item.c_productID]

        return {
            ...product,
            ...item,
            image
        }
    })

    const categorizedProducts = shipmentData?.[0]?.categorizedProducts

    return (
        <>
            {giftCertificates.map((giftCertificateItem, index) => {
                return (
                    <React.Fragment key={index}>
                        {(!!categorizedProducts?.regularProducts?.length ||
                            !!categorizedProducts?.bonusProducts?.length) &&
                            (index !== giftCertificates?.length - 1 ||
                                giftCertificates?.length === 1) && (
                                <Divider
                                    color="gray.700"
                                    margin="30px 0px 30px 0px"
                                    borderColor="gray.700"
                                    className="divider-line"
                                    orientation="horizontal"
                                />
                            )}
                        <Box
                            key={index}
                            position="relative"
                            className="sf-cart-item"
                            data-uuid={giftCertificateItem.c_sku}
                            data-testid={`sf-cart-item-${
                                giftCertificateItem.c_productID
                                    ? giftCertificateItem.c_productID
                                    : ''
                            }`}
                        >
                            <Stack
                                layerStyle="cardBordered"
                                align="flex-start"
                                className="cart-item-stack-wrapper"
                            >
                                <Flex
                                    alignItems="flex-start"
                                    backgroundColor="white"
                                    className="cart-item-flex"
                                    {...cartStyles.lineItems.item.itemFlex}
                                >
                                    <Box
                                        className="item-image-wrapper gift-certificate-item"
                                        display="flex"
                                        flexDirection="column"
                                        alignItems="center"
                                    >
                                        <Box
                                            className="image-box-wrapper"
                                            {...cartStyles.lineItems.item.imageBox.base}
                                        >
                                            <Box
                                                className="image-box"
                                                {...cartStyles.checkoutSummarylineItems
                                                    .imageWrapper}
                                            >
                                                {giftCertificateItem.image && (
                                                    <Image
                                                        alt="Gift Card Image"
                                                        className="product-image"
                                                        src={giftCertificateItem.image}
                                                        ignoreFallback={true}
                                                        {...cartStyles.checkoutSummarylineItems
                                                            .giftcertImage}
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Stack
                                        className="item-stack"
                                        {...cartStyles.lineItems.item.itemStack}
                                    >
                                        <Stack
                                            spacing={1}
                                            className="name-price-stack"
                                            {...cartStyles.lineItems.item.namePriceStack.base}
                                        >
                                            <Box
                                                display="grid"
                                                gap="20px"
                                                className="name-variation-badge"
                                            >
                                                <Box
                                                    className="name-variation-box"
                                                    {...cartStyles.lineItems.item.nameVariation}
                                                >
                                                    <Box
                                                        className="name-box"
                                                        display="flext"
                                                        justifyContent="space-between"
                                                        {...cartStyles.lineItems.item.nameWrapper
                                                            .base}
                                                    >
                                                        <Heading
                                                            as="h2"
                                                            fontSize="md"
                                                            display="flext"
                                                            className="product-name-heading"
                                                            {...cartStyles.checkoutSummarylineItems
                                                                .giftcertName}
                                                        >
                                                            <FormattedMessage
                                                                id="gift.certificate"
                                                                defaultMessage="Gift Certificate"
                                                            />
                                                        </Heading>
                                                    </Box>
                                                    <Box>
                                                        <Text
                                                            as="span"
                                                            display="flex"
                                                            {...cartStyles.checkoutSummarylineItems
                                                                .giftcertQuantity}
                                                        >
                                                            <FormattedMessage
                                                                defaultMessage="Quantity: 1"
                                                                id="order_summary.giftcert.quantity"
                                                            />
                                                        </Text>
                                                    </Box>
                                                    <Box>
                                                        <Text
                                                            as="span"
                                                            display="flex"
                                                            {...cartStyles.checkoutSummarylineItems
                                                                .giftcertPrice}
                                                        >
                                                            {
                                                                giftCertificateItem.c_selectedCurrencySymbol
                                                            }
                                                            {giftCertificateItem.amount.toFixed(2)}
                                                        </Text>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Stack>
                                    </Stack>
                                </Flex>
                            </Stack>
                        </Box>
                    </React.Fragment>
                )
            })}
        </>
    )
}

GiftCertificateItemSummary.propTypes = {
    giftCertificateItems: PropTypes.arrayOf(PropTypes.object),
    shipmentData: PropTypes.any
}

export default GiftCertificateItemSummary
